/**
 * Cbill — API usage, quotas, and invoice.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Receipt, Gauge, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CbillPage() {
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cbill/plan`, { withCredentials: true }),
      axios.get(`${API}/cbill/usage`, { withCredentials: true }),
      axios.get(`${API}/cbill/invoice`, { withCredentials: true }),
    ]).then(([p, u, i]) => {
      setPlan(p.data); setUsage(u.data); setInvoice(i.data);
    }).catch(() => {});
  }, []);

  if (!usage) return <div style={{ color: "#94a3b8" }}>Loading…</div>;

  return (
    <div data-testid="cbill-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", marginBottom: 6 }}>
        Cbill · Usage & Billing
      </h1>
      <p style={{ color: "#64748b", fontSize: 13, marginBottom: 22 }}>
        Per-tenant API metering, quotas, and invoicing. Every billable event is linked to its consent + audit trail.
      </p>

      {/* Plan summary */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginBottom: 18 }}>
        <div className="flex items-center justify-between">
          <div>
            <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7 }}>Plan</div>
            <div style={{ color: "#f1f5f9", fontSize: 20, fontWeight: 800 }}>
              {plan?.plan_name || "starter"}
            </div>
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7 }}>Monthly Quota</div>
            <div style={{ color: "#f1f5f9", fontSize: 20, fontWeight: 800, textAlign: "right" }}>
              {usage.total_used.toLocaleString()} / {usage.total_quota.toLocaleString()}
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 14, background: "#080c12", borderRadius: 8, height: 10, overflow: "hidden",
        }}>
          <div data-testid="cbill-total-progress" style={{
            width: `${Math.min(100, usage.percent_used)}%`, height: "100%",
            background: usage.percent_used > 90 ? "#ef4444" :
                        usage.percent_used > 70 ? "#f59e0b" : "#10b981",
            transition: "width 0.4s ease",
          }} />
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8" }}>
          {usage.percent_used}% of monthly quota used · Remaining: {usage.remaining_total?.toLocaleString() || "∞"}
        </div>
      </div>

      {/* Module breakdown */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20, marginBottom: 18 }}>
        <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 14 }}>
          Per-module Usage
        </h3>
        <table style={{ width: "100%", fontSize: 12, color: "#cbd5e1" }}>
          <thead>
            <tr style={{ color: "#64748b", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
              <th>Module</th><th>Used</th><th>Quota</th><th>Remaining</th><th style={{ width: 130 }}>Usage</th>
            </tr>
          </thead>
          <tbody>
            {(usage.modules || []).map((m) => (
              <tr key={m.module} data-testid={`cbill-row-${m.module}`}
                  style={{ borderTop: "1px solid #1e2530" }}>
                <td style={{ padding: "8px 0" }}><code style={{ color: "#22d3ee" }}>{m.module}</code></td>
                <td>{m.used}</td>
                <td>{m.quota}</td>
                <td>{m.remaining}</td>
                <td>
                  <div style={{ background: "#080c12", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, m.percent_used)}%`, height: "100%",
                      background: m.percent_used > 90 ? "#ef4444" :
                                  m.percent_used > 70 ? "#f59e0b" : "#10b981",
                    }} />
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{m.percent_used}%</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invoice preview */}
      {invoice && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 20 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, margin: 0,
                          display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Receipt size={14} /> Current Invoice (this month)
            </h3>
            <div style={{ color: "#10b981", fontWeight: 800, fontSize: 18 }}>
              ₹{(invoice.total_cost_inr || 0).toLocaleString()}
            </div>
          </div>
          <table style={{ width: "100%", fontSize: 12, color: "#cbd5e1" }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6 }}>
                <th>Module</th><th>Count</th><th>Cost (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lines || []).map((l, i) => (
                <tr key={i} data-testid={`invoice-line-${i}`}
                    style={{ borderTop: "1px solid #1e2530" }}>
                  <td style={{ padding: "8px 0" }}><code style={{ color: "#a855f7" }}>{l.module}</code></td>
                  <td>{l.count}</td>
                  <td>₹{l.cost_inr.toLocaleString()}</td>
                </tr>
              ))}
              {(invoice.lines || []).length === 0 && (
                <tr><td colSpan={3} style={{ padding: 16, textAlign: "center", color: "#64748b" }}>
                  No billable events yet this month
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
